import React, { useId, useState } from 'react';
import { Spinner } from '@/components/ui';
import { uploadHiredEquipmentRowAttachment } from '../../../services/hiredEquipmentAttachmentService';
import type { HiredEquipmentRowAttachment } from '../types';
import { HE_ATTACHMENT_ACCEPT, attachmentFromDto, isAllowedHiredEquipmentAttachment } from '../utils/rowAttachment';

interface RowAttachmentCellProps {
  attachment?: HiredEquipmentRowAttachment | null;
  lineNumber: number;
  onChange: (attachment: HiredEquipmentRowAttachment | null) => void;
}

const RowAttachmentCell: React.FC<RowAttachmentCellProps> = ({ attachment, lineNumber, onChange }) => {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!isAllowedHiredEquipmentAttachment(file)) {
      setError('Images and PDFs only');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const saved = await uploadHiredEquipmentRowAttachment(file);
      onChange(attachmentFromDto(saved));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="tw-min-w-[7.5rem]">
      <input
        id={inputId}
        type="file"
        accept={HE_ATTACHMENT_ACCEPT}
        className="tw-sr-only"
        aria-label={`Attachment, line ${lineNumber}`}
        disabled={uploading}
        onChange={handleChange}
      />
      {uploading ? (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-content-muted">
          <Spinner animation="border" size="sm" aria-hidden="true" />
          Uploading…
        </span>
      ) : attachment ? (
        <div className="tw-flex tw-items-center tw-gap-1">
          <span className="tw-min-w-0 tw-truncate tw-text-sm tw-text-content" title={attachment.filename}>
            <i className="bi bi-paperclip tw-mr-1 tw-text-content-muted" aria-hidden="true" />
            {attachment.filename}
          </span>
          <button
            type="button"
            className="tw-inline-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-content-muted hover:tw-text-danger"
            aria-label={`Remove attachment, line ${lineNumber}`}
            onClick={() => {
              setError(null);
              onChange(null);
            }}
          >
            ×
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="tw-mb-0 tw-inline-flex tw-min-h-[2rem] tw-cursor-pointer tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-primary hover:tw-underline"
        >
          <i className="bi bi-paperclip" aria-hidden="true" />
          Attach
        </label>
      )}
      {error ? (
        <p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default RowAttachmentCell;
